
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Language, translations, defaultLanguage } from '@/translations';

type LanguageContextType = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, fallback?: string) => string;
};

const LanguageContext = createContext<LanguageContextType | null>(null);

interface LanguageProviderProps {
  children: ReactNode;
  initialLanguage?: Language;
}

const getInitialLanguage = (): Language => {
  const browserLang = navigator.language.toLowerCase();
  
  // Check for language in localStorage first
  const savedLanguage = localStorage.getItem('preferredLanguage') as Language | null;
  if (savedLanguage && Object.keys(translations).includes(savedLanguage)) {
    return savedLanguage as Language;
  }
  
  // If no saved language, detect from browser
  if (browserLang.startsWith('ja')) {
    return 'ja';
  } else if (browserLang.startsWith('zh-cn') || browserLang === 'zh-hans') {
    return 'zh-CN';
  } else if (browserLang.startsWith('zh-tw') || browserLang === 'zh-hant') {
    return 'zh-TW';
  } else if (browserLang.startsWith('ko')) {
    return 'ko';
  }
  return 'en';
};

const LanguageProvider = ({ children, initialLanguage }: LanguageProviderProps) => {
  const [language, setLanguageState] = useState<Language>(initialLanguage || getInitialLanguage());

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    // 選択された言語をlocalStorageに保存
    localStorage.setItem('preferredLanguage', lang);
  };

  const t = (key: string, fallback?: string) => {
    const translation = translations[language][key];
    return translation || fallback || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === null) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { LanguageContext, LanguageProvider, useLanguage };
